import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, MapPin, Shield, Check, Heart, ChevronLeft, ChevronRight, X, Expand, Navigation } from 'lucide-react';
import Navbar from "../components/Navbar";
import LoadingScreen from "../components/LoadingScreen";
import { useAuth } from '../context/AuthContext';
import { useJsonLd } from '../hooks/useJsonLd';
import { formatDOP } from '../lib/currency';
import { ZONE_COORDS, directionsUrl } from '../lib/zones';

// Leaflet (~45 KB gzip) se carga en su propio chunk solo cuando la ficha
// tiene una zona mapeable — no infla el bundle del resto de la app.
const DeliveryMap = React.lazy(() => import('../components/DeliveryMap'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ListingDetail() {
  const { t } = useTranslation();
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [bookingData, setBookingData] = useState({
    startDate: '',
    startTime: '',
    hours: 4
  });
  const [bookingError, setBookingError] = useState('');
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAsset(null);
    setLoadError(false);
    fetch(`${BACKEND_URL}/api/assets/${assetId}`)
      .then(res => {
        if (!res.ok) throw new Error('Asset not found');
        return res.json();
      })
      .then(data => { if (!cancelled) setAsset(data); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [assetId, retry]);

  // Favoritos: comprueba si el activo ya está en la lista de deseos del cliente
  useEffect(() => {
    if (!user || user.role !== 'client' || !assetId) return;
    fetch(`${BACKEND_URL}/api/client/favorites`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : []))
      .then(data => { if (Array.isArray(data)) setIsFavorite(data.some(f => f.asset_id === assetId)); })
      .catch(() => {});
  }, [user, assetId]);

  const toggleFavorite = async () => {
    if (!user || user.role !== 'client') { navigate(`/login?redirect=/listing/${assetId}`); return; }
    setFavLoading(true);
    try {
      if (isFavorite) {
        await fetch(`${BACKEND_URL}/api/client/favorites/${assetId}`, { method: 'DELETE', credentials: 'include' });
        setIsFavorite(false);
      } else {
        await fetch(`${BACKEND_URL}/api/client/favorites`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ asset_id: assetId }),
        });
        setIsFavorite(true);
      }
    } catch { /* noop */ }
    finally { setFavLoading(false); }
  };

  const photos = (asset?.images?.length > 0)
    ? asset.images
    : [{ url: `${process.env.PUBLIC_URL}/images/placeholder.jpg` }];
  const photoCount = photos.length;
  const goToPhoto = (i) => setActivePhoto(((i % photoCount) + photoCount) % photoCount);
  const nextPhoto = () => goToPhoto(activePhoto + 1);
  const prevPhoto = () => goToPhoto(activePhoto - 1);

  // Si cambia el vehículo, volver a la primera foto
  useEffect(() => { setActivePhoto(0); }, [asset?.id]);

  // Flechas del teclado para navegar (cómodo en escritorio y en el lightbox)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') nextPhoto();
      else if (e.key === 'ArrowLeft') prevPhoto();
      else if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePhoto, photoCount]);

  const jsonLdData = useMemo(() => {
    if (!asset) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: asset.name,
      description: asset.description,
      image: asset.images?.[0]?.url,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: asset.price_per_day,
        availability: asset.is_active
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        seller: { '@type': 'Organization', name: 'RedMobility' },
      },
      ...(asset.total_rentals > 0 && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: asset.rating,
          reviewCount: asset.total_rentals,
          bestRating: 5,
          worstRating: 1,
        },
      }),
    };
  }, [asset]);

  useJsonLd(jsonLdData);

  if (loadError) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 flex flex-col items-center justify-center text-center min-h-[70vh]">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#111827] mb-3">
            {t('listing.loadErrorTitle', 'No pudimos cargar este vehículo')}
          </h1>
          <p className="text-gray-500 max-w-md mb-8">
            {t('listing.loadErrorDesc', 'Puede que ya no esté disponible o haya un problema de conexión. Inténtalo de nuevo.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setRetry(r => r + 1)}
              className="px-7 py-3 rounded-xl bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white font-bold shadow-md hover:scale-105 transition-transform"
            >
              {t('common.retry', 'Reintentar')}
            </button>
            <button
              onClick={() => navigate('/search')}
              className="px-7 py-3 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
            >
              {t('search.emptyExploreAll', 'Ver todas las categorías')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <LoadingScreen inline className="min-h-[70vh]" message={t('listing.loadingVehicle', 'Cargando vehículo...')} />
      </div>
    );
  }

  // max(1,...) espeja el backend (bookings.py): evita tarifa horaria de RD$0
  const hourPrice = asset.price_per_hour ?? (asset.price_per_day ? Math.max(1, Math.floor(asset.price_per_day / 8)) : null);
  const totalPrice = hourPrice != null
    ? (bookingData.hours >= 24
        ? Math.floor(bookingData.hours / 24) * asset.price_per_day + (bookingData.hours % 24) * hourPrice
        : bookingData.hours * hourPrice)
    : null;
  // Tasa real del proveedor (la API la calcula con overrides del admin);
  // 0.15 solo como respaldo si el backend aún no la envía.
  const commissionRate = asset.commission_rate ?? 0.15;

  const today = new Date().toISOString().split('T')[0];

  const handleBooking = () => {
    if (!user) {
      navigate(`/login?redirect=/listing/${assetId}`);
      return;
    }
    if (user.role === 'provider' || user.role === 'admin') {
      setBookingError(t('listing.providerOrAdminError'));
      return;
    }
    if (!bookingData.startDate || !bookingData.startTime) {
      setBookingError(t('listing.selectDateTimeError'));
      return;
    }
    setBookingError('');
    localStorage.setItem('bookingData', JSON.stringify({
      asset,
      ...bookingData,
      totalPrice
    }));
    navigate('/checkout');
  };

  const openMobileSheet = () => {
    setBookingError('');
    setMobileSheetOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <button
          onClick={() => navigate('/search')}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition-colors"
        >
          {t('listing.backShort')}
        </button>

        {/* Galería unificada: imagen grande + flechas + contador + miniaturas */}
        <div className="mb-8">
          <div className="relative w-full aspect-[16/9] rounded-3xl overflow-hidden bg-gray-100 group">
            <img
              key={photos[activePhoto].url}
              src={photos[activePhoto].url}
              alt={activePhoto === 0 ? asset.name : t('listing.photoAlt', { name: asset.name, n: activePhoto + 1 })}
              width="1000"
              height="562"
              fetchpriority={activePhoto === 0 ? 'high' : undefined}
              className="w-full h-full object-contain cursor-zoom-in"
              onClick={() => setLightboxOpen(true)}
            />

            {/* Botón ampliar */}
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              aria-label={t('listing.expandPhoto', 'Ampliar foto')}
              className="absolute top-3 right-3 bg-black/45 hover:bg-black/65 text-white rounded-full p-2 transition-colors"
            >
              <Expand className="w-4 h-4" />
            </button>

            {photoCount > 1 && (
              <>
                {/* Contador */}
                <div className="absolute bottom-3 right-3 bg-black/55 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                  {activePhoto + 1} / {photoCount}
                </div>
                {/* Flechas: siempre visibles en móvil, al hover en escritorio */}
                <button
                  type="button"
                  onClick={prevPhoto}
                  aria-label={t('listing.prevPhoto', 'Foto anterior')}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-[#134168] rounded-full p-2 shadow-md transition-all md:opacity-0 md:group-hover:opacity-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={nextPhoto}
                  aria-label={t('listing.nextPhoto', 'Siguiente foto')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-[#134168] rounded-full p-2 shadow-md transition-all md:opacity-0 md:group-hover:opacity-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Tira de miniaturas de TODAS las fotos */}
          {photoCount > 1 && (
            <div className="flex gap-2.5 mt-3 overflow-x-auto hide-scrollbar pb-1">
              {photos.map((img, i) => (
                <button
                  key={img.url}
                  type="button"
                  onClick={() => goToPhoto(i)}
                  aria-label={t('listing.goToPhoto', { n: i + 1 })}
                  aria-current={i === activePhoto}
                  className={`shrink-0 w-20 h-16 md:w-24 md:h-[68px] rounded-xl overflow-hidden border-2 transition-all ${
                    i === activePhoto ? 'border-[#134168] ring-2 ring-[#134168]/20' : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.url}
                    alt={t('listing.photoAlt', { name: asset.name, n: i + 1 })}
                    width="96"
                    height="68"
                    loading="lazy"
                    className="w-full h-full object-cover bg-gray-100"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Lightbox a pantalla completa */}
        {lightboxOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              aria-label={t('common.close', 'Cerrar')}
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            >
              <X className="w-7 h-7" />
            </button>
            <img
              src={photos[activePhoto].url}
              alt={t('listing.photoAlt', { name: asset.name, n: activePhoto + 1 })}
              className="max-w-[92vw] max-h-[86vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {photoCount > 1 && (
              <>
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/90 text-sm font-medium">
                  {activePhoto + 1} / {photoCount}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                  aria-label={t('listing.prevPhoto', 'Foto anterior')}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-3 transition-colors"
                >
                  <ChevronLeft className="w-7 h-7" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                  aria-label={t('listing.nextPhoto', 'Siguiente foto')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-3 transition-colors"
                >
                  <ChevronRight className="w-7 h-7" />
                </button>
              </>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium capitalize">{asset.type.replace('_', ' ')}</span>
                <span className="text-gray-500">• {asset.location_zone}</span>
              </div>
              <div className="flex items-start justify-between gap-3 mb-4">
                <h1 className="text-4xl font-heading font-bold text-[#111827]">{asset.name}</h1>
                {user?.role === 'client' && (
                  <button
                    onClick={toggleFavorite}
                    disabled={favLoading}
                    title={isFavorite ? t('listing.removeFavorite', 'Quitar de favoritos') : t('listing.addFavorite', 'Guardar en favoritos')}
                    aria-label={isFavorite ? t('listing.removeFavorite', 'Quitar de favoritos') : t('listing.addFavorite', 'Guardar en favoritos')}
                    className="p-2.5 border border-gray-200 rounded-full hover:bg-gray-50 transition shrink-0 mt-1 disabled:opacity-50"
                  >
                    <Heart className={`w-6 h-6 transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  {asset.total_rentals > 0 ? (
                    <>
                      <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" aria-hidden="true" />
                      <span className="font-bold">{asset.rating}</span>
                      <span className="text-gray-500">({asset.total_rentals} {t('listing.rentalsCountLabel2')})</span>
                    </>
                  ) : (
                    <span className="text-sm text-[#134168] font-medium">{t('homepage.newOnPlatform')}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-600">{asset.pickup_address}</span>
                </div>
              </div>
            </div>

            {asset.provider && (
              <div className="border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  {asset.provider.picture ? (
                    <img src={asset.provider.picture} alt={asset.provider.name} className="w-16 h-16 rounded-full object-cover" />
                  ) : (
                    /* BUG-004 (ISO 25010 Fase 2): avatar local con inicial — el placeholder externo no cargaba */
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#36C0C7] to-[#134168] flex items-center justify-center text-white text-2xl font-bold shrink-0">
                      {(asset.provider.name || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg">{asset.provider.name}</h3>
                      {asset.provider.verified && <Shield className="w-5 h-5 text-green-500" />}
                    </div>
                    <p className="text-sm text-gray-500">{t('listing.verifiedProviderLabel')}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <h2 className="text-2xl font-heading font-bold mb-4">{t('listing.description')}</h2>
              <p className="text-gray-700 leading-relaxed">{asset.description}</p>
            </div>

            {Array.isArray(asset.what_included) && asset.what_included.length > 0 && (
              <div>
                <h2 className="text-2xl font-heading font-bold mb-4">{t('listing.whatIncludes')}</h2>
                <div className="grid grid-cols-2 gap-3">
                  {asset.what_included.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ZONE_COORDS[asset.location_zone] && (
              <div>
                <h2 className="text-2xl font-heading font-bold mb-4">{t('listing.deliveryPoint', 'Punto de entrega')}</h2>
                <Suspense fallback={<div className="w-full h-64 md:h-80 rounded-2xl bg-gray-100 animate-pulse" />}>
                  <DeliveryMap zone={asset.location_zone} address={asset.pickup_address} />
                </Suspense>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-3">
                  <p className="text-sm text-gray-500 flex-1">
                    {t('listing.deliveryApprox', 'Ubicación aproximada de la zona. La dirección exacta se coordina con el proveedor al confirmar la reserva.')}
                  </p>
                  <a
                    href={directionsUrl(asset.pickup_address, asset.location_zone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#134168] text-white rounded-full font-medium hover:bg-[#39b4aa] transition-colors shrink-0"
                  >
                    <Navigation className="w-4 h-4" />
                    {t('listing.getDirections', 'Cómo llegar')}
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="hidden md:block lg:sticky lg:top-24 h-fit">
            <div className="border border-gray-200 rounded-3xl p-6 shadow-xl">
              <div className="mb-6">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-bold text-[#134168]">{formatDOP(asset.price_per_day)}</span>
                  <span className="text-gray-500">{t('listing.perDayShort2')}</span>
                </div>
                <p className="text-sm text-gray-500">{t('listing.perHourPrice', { price: formatDOP(asset.price_per_hour) })}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('checkout.date')}</label>
                  <input
                    type="date"
                    value={bookingData.startDate}
                    min={today}
                    onChange={(e) => setBookingData({...bookingData, startDate: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('listing.startTime')}</label>
                  <input
                    type="time"
                    value={bookingData.startTime}
                    onChange={(e) => setBookingData({...bookingData, startTime: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('listing.durationHours')}</label>
                  <select
                    value={bookingData.hours}
                    onChange={(e) => setBookingData({...bookingData, hours: Number(e.target.value)})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                  >
                    {[1,2,3,4,6,8,12,24,48].map(h => (
                      <option key={h} value={h}>{h} {h === 1 ? t('listing.hour') : t('bookingConfirmation.hours')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border-t border-gray-200 mt-6 pt-6">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">{t('common.subtotal')}</span>
                  <span className="font-semibold">{totalPrice != null ? formatDOP(totalPrice) : t('listing.askPrice')}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">{t('listing.platformFee')}</span>
                  <span className="font-semibold">{totalPrice != null ? formatDOP(Math.round(totalPrice * commissionRate)) : '—'}</span>
                </div>
                <div className="flex justify-between text-lg font-bold mt-4">
                  <span>{t('common.total')}</span>
                  <span className="text-[#134168]">{totalPrice != null ? formatDOP(totalPrice + Math.round(totalPrice * commissionRate)) : t('listing.askPrice')}</span>
                </div>
              </div>

              <button
                onClick={handleBooking}
                disabled={!bookingData.startDate || !bookingData.startTime}
                className="w-full bg-[#134168] text-white py-4 rounded-full font-bold text-lg hover:bg-[#0d2d47] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95 mt-6"
              >
                {!user ? t('listing.signInToReserve') : t('listing.reserveNow')}
              </button>
              <p className="text-xs text-center text-gray-500 mt-3">{t('listing.noChargeUntilConfirm')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex items-center justify-between z-40">
        <div>
          <p className="text-xl font-bold text-[#134168]">{formatDOP(asset.price_per_day)}<span className="text-xs font-normal text-gray-500"> {t('listing.perDayShort2')}</span></p>
          {asset.price_per_hour && <p className="text-xs text-gray-400">{formatDOP(asset.price_per_hour)} {t('listing.perHourShort')}</p>}
        </div>
        <button
          id="mobile-book-now-btn"
          onClick={openMobileSheet}
          className="bg-[#134168] text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-[#39b4aa] transition-all active:opacity-75"
        >
          {t('listing.reserveNow')}
        </button>
      </div>

      {mobileSheetOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-50"
            onClick={() => setMobileSheetOpen(false)}
          />
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 shadow-2xl"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-5 pb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">{t('listing.bookNow2')}</h3>
                <button onClick={() => setMobileSheetOpen(false)} className="p-2 rounded-full hover:bg-gray-100 active:opacity-75">
                  <span className="text-gray-500 text-lg">×</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('checkout.date')}</label>
                  <input
                    type="date"
                    value={bookingData.startDate}
                    min={today}
                    onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('listing.startTime')}</label>
                  <input
                    type="time"
                    value={bookingData.startTime}
                    onChange={(e) => setBookingData({ ...bookingData, startTime: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('bookingConfirmation.duration')}</label>
                  <select
                    value={bookingData.hours}
                    onChange={(e) => setBookingData({ ...bookingData, hours: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] text-gray-900"
                  >
                    {[1,2,3,4,6,8,12,24,48].map(h => (
                      <option key={h} value={h}>{h} {h === 1 ? t('listing.hour') : t('bookingConfirmation.hours')}</option>
                    ))}
                  </select>
                </div>

                {bookingData.startDate && bookingData.startTime && totalPrice != null && (
                  <div className="flex justify-between items-center bg-gray-50 rounded-xl px-4 py-3">
                    <span className="text-sm text-gray-600">{t('listing.estimatedTotal')}</span>
                    <span className="font-bold text-[#134168]">{formatDOP(totalPrice + Math.round(totalPrice * commissionRate))}</span>
                  </div>
                )}

                {bookingError && (
                  <p className="text-sm text-red-600 font-medium">{bookingError}</p>
                )}

                <button
                  id="mobile-sheet-confirm-btn"
                  onClick={handleBooking}
                  disabled={!bookingData.startDate || !bookingData.startTime}
                  className="w-full bg-[#134168] text-white py-4 rounded-full font-bold text-base hover:bg-[#39b4aa] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all active:opacity-75 min-h-[52px]"
                >
                  {t('listing.confirmContinue')}
                </button>
                <p className="text-xs text-center text-gray-500">{t('listing.noChargeUntilConfirm')}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
